import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const OPTIONS_LIMIT = 15;

const filterOptions = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
});

const compactInputSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const compactAutoSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
    pr: "32px !important",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px !important",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const getPartyLabel = (party) =>
  party?.partydetails2 || party?.partydetails || "";

const finishCellEdit = (api, id, field) => {
  if (typeof api?.stopCellEditMode === "function") {
    api.stopCellEditMode({ id, field });
    return;
  }

  if (typeof api?.commitCellChange === "function") {
    api.commitCellChange({ id, field });
  }

  if (typeof api?.setCellMode === "function") {
    api.setCellMode(id, field, "view");
  }
};

function GridTopBar({ rowCount }) {
  return (
    <GridToolbarContainer>
      <Box
        sx={{
          px: 1,
          py: 0.5,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f7fbff",
          borderBottom: "1px solid #dbe7f3",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 11.5 }}>
          Rows: {rowCount}
        </Typography>
      </Box>
    </GridToolbarContainer>
  );
}

function PartEditAutocomplete(props) {
  const { id, field, value, api, allparts } = props;

  const selected =
    allparts.find(
      (p) =>
        String(p.itemcode || "").toUpperCase() ===
          String(value || "").toUpperCase() || p.itemid === value
    ) || null;

  return (
    <Autocomplete
      size="small"
      options={allparts}
      value={selected}
      autoHighlight
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        option ? `${option.itemcode || ""} - ${option.itemname || ""}` : ""
      }
      isOptionEqualToValue={(option, val) => option?.itemid === val?.itemid}
      onChange={async (event, newValue) => {
        const item = newValue || null;

        await api.setEditCellValue({
          id,
          field: "itemid",
          value: item?.itemid || null,
        });

        await api.setEditCellValue({
          id,
          field,
          value: item?.itemcode || "",
        });

        await api.setEditCellValue({
          id,
          field: "itemname",
          value: item?.itemname || "",
        });

        finishCellEdit(api, id, field);
      }}
      renderOption={(props2, option) => (
        <li {...props2} key={option.itemid || option.itemcode}>
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.15 }}
            >
              {option.itemcode} - {option.itemname}
            </Typography>
            <Typography
              sx={{ fontSize: 10.25, color: "text.secondary", lineHeight: 1.1 }}
            >
              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
              {option.rackname || "N/A"}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} autoFocus size="small" sx={compactAutoSx} />
      )}
    />
  );
}

function PartyEditAutocomplete(props) {
  const { id, field, value, api, parties } = props;

  const selected =
    parties.find(
      (p) =>
        String(getPartyLabel(p)).toUpperCase() ===
          String(value || "").toUpperCase() || p.subledgerid === value
    ) || null;

  return (
    <Autocomplete
      size="small"
      options={parties}
      value={selected}
      autoHighlight
      filterOptions={filterOptions}
      getOptionLabel={(option) => getPartyLabel(option)}
      isOptionEqualToValue={(option, val) =>
        option?.subledgerid === val?.subledgerid
      }
      onChange={async (event, newValue) => {
        const party = newValue || null;

        await api.setEditCellValue({
          id,
          field: "subledgerid",
          value: party?.subledgerid || null,
        });

        await api.setEditCellValue({
          id,
          field,
          value: getPartyLabel(party),
        });

        finishCellEdit(api, id, field);
      }}
      renderOption={(props2, option) => (
        <li {...props2} key={option.subledgerid}>
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.15 }}
            >
              {getPartyLabel(option)}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} autoFocus size="small" sx={compactAutoSx} />
      )}
    />
  );
}

function DateEditCell(props) {
  const { id, field, value, api } = props;

  return (
    <TextField
      type="date"
      size="small"
      value={value ? dayjs(value).format("YYYY-MM-DD") : ""}
      onChange={async (e) => {
        await api.setEditCellValue({
          id,
          field,
          value: e.target.value || null,
        });
      }}
      onBlur={() => {
        finishCellEdit(api, id, field);
      }}
      fullWidth
      sx={compactInputSx}
    />
  );
}

const WarrantyReq = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const [disableSave, setDisableSave] = useState(false);
  const [disableUpdate, setDisableUpdate] = useState(false);

  const [docdate, setDocDate] = useState(dayjs());
  const [docno, setDocNo] = useState("");
  const [mode, setMode] = useState(1);

  const [searchdisabled, setSearchDisabled] = useState(true);
  const [searchbtndisabled, setSearchBtnDisabled] = useState(true);

  const [docopts, setDocOpts] = useState([]);
  const [docdetails, setDocDetails] = useState(null);

  const [reqremarks, setReqRemarks] = useState("");
  const [allparts, setAllParts] = useState([]);
  const [parties, setParties] = useState([]);
  const [rows, setRows] = useState([]);

  const [loadingParts, setLoadingParts] = useState(false);
  const [loadingParties, setLoadingParties] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [ticketno, setTicketNo] = useState("");
  const [itemInput, setItemInput] = useState("");
  const [itemValue, setItemValue] = useState(null);
  const [againstitemsrlno, setAgainstItemSrlNo] = useState("");
  const [customername, setCustomerName] = useState("");
  const [phono, setPhono] = useState("");
  const [vehicleno, setVehicleNo] = useState("");
  const [dateofsale, setDateOfSale] = useState(null);
  const [partyInput, setPartyInput] = useState("");
  const [partyValue, setPartyValue] = useState(null);
  const [rowRemarks, setRowRemarks] = useState("");

  const ticketRef = useRef(null);
  const itemRef = useRef(null);
  const againstSrlRef = useRef(null);
  const customerRef = useRef(null);
  const phoneRef = useRef(null);
  const vehicleRef = useRef(null);
  const partyRef = useRef(null);
  const rowRemarksRef = useRef(null);

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
    return null;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);
    return () => clearTimeout(timeout);
  }, []);

  const renumberRows = (arr) =>
    arr.map((row, idx) => ({
      ...row,
      srl: idx + 1,
    }));

  const normalizeRowsForGrid = (
    inputRows = [],
    parts = allparts,
    partyList = parties
  ) =>
    (Array.isArray(inputRows) ? inputRows : []).map((r, idx) => {
      const matchedItem =
        parts.find(
          (p) =>
            p.itemid === r.itemid ||
            String(p.itemcode || "").toUpperCase() ===
              String(r.itemcode || r.item || "").toUpperCase()
        ) || null;

      const matchedParty =
        partyList.find((p) => p.subledgerid === r.subledgerid) || null;

      return {
        srl: idx + 1,
        ticketno: r.ticketno || "",
        itemid: matchedItem?.itemid || r.itemid || null,
        itemcode: matchedItem?.itemcode || r.itemcode || "",
        itemname: matchedItem?.itemname || r.itemname || "",
        againstitemsrlno: r.againstitemsrlno || "",
        customername: r.customername || "",
        phono: String(r.phono || "").replace(/\D/g, ""),
        vehicleno: r.vehicleno || "",
        dateofsale: r.dateofsale || null,
        subledgerid: r.subledgerid || null,
        partylabel:
          getPartyLabel(matchedParty) ||
          r.partylabel ||
          r.partydetails2 ||
          r.partydetails ||
          "",
        remarks: r.remarks || "",
      };
    });

  const resetEntryFields = () => {
    setTicketNo("");
    setItemInput("");
    setItemValue(null);
    setAgainstItemSrlNo("");
    setCustomerName("");
    setPhono("");
    setVehicleNo("");
    setDateOfSale(null);
    setPartyInput("");
    setPartyValue(null);
    setRowRemarks("");

    setTimeout(() => {
      if (ticketRef.current) ticketRef.current.focus();
    }, 50);
  };

  async function getAllParts() {
    try {
      setLoadingParts(true);
      const response = await fetch("/warranty/req/allparts", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setAllParts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load parts");
    } finally {
      setLoadingParts(false);
    }
  }

  async function getAllParty() {
    try {
      setLoadingParties(true);
      const response = await fetch("/warranty/req/partydata", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setParties(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load parties");
    } finally {
      setLoadingParties(false);
    }
  }

  async function getDocNos() {
    try {
      const response = await fetch("/warranty/req/documentnos", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setDocOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getDocNos();
    getAllParts();
    getAllParty();
  }, []);

  useEffect(() => {
    if (!rows.length || !parties.length) return;

    setRows((prev) =>
      prev.map((row) => {
        if (row.subledgerid && !row.partylabel) {
          const matchedParty =
            parties.find((p) => p.subledgerid === row.subledgerid) || null;
          return {
            ...row,
            partylabel: matchedParty ? getPartyLabel(matchedParty) : "",
          };
        }
        return row;
      })
    );
  }, [parties]);

  const handleModeChange = (e) => {
    const selectedMode = parseInt(e.target.value, 10);
    setMode(selectedMode);

    if (selectedMode === 1) {
      setSearchDisabled(true);
      setSearchBtnDisabled(true);
      setDocDetails(null);
      setDocNo("");
      setRows([]);
      setReqRemarks("");
      setDocDate(dayjs());
      resetEntryFields();
    } else {
      setSearchDisabled(false);
      setSearchBtnDisabled(false);
    }
  };

  const fetchPartByCode = async (code) => {
    try {
      const body = { code: String(code || "").trim() };
      if (!body.code) return null;

      const response = await fetch("/warranty/req/allparts/detailfetch", {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data?.type === "error" || !data?.itemid) return null;
      return data;
    } catch (err) {
      console.error(err.message);
      return null;
    }
  };

  const resolvePartFromInput = async (typedValue, selectedPart) => {
    if (selectedPart?.itemid) return selectedPart;

    const typed = String(typedValue || "").trim();
    if (!typed) return null;

    const localMatch =
      allparts.find(
        (item) =>
          String(item.itemcode || "").toUpperCase() === typed.toUpperCase()
      ) || null;

    if (localMatch) return localMatch;

    const fetched = await fetchPartByCode(typed);
    return fetched || null;
  };

  const resolvePartyFromInput = (typedValue, selectedParty) => {
    if (selectedParty?.subledgerid) return selectedParty;

    const typed = String(typedValue || "").trim();
    if (!typed) return null;

    const normalized = typed.toUpperCase();

    return (
      parties.find(
        (p) =>
          String(getPartyLabel(p)).toUpperCase() === normalized ||
          String(p.partydetails || "").toUpperCase() === normalized ||
          String(p.partydetails2 || "").toUpperCase() === normalized
      ) || null
    );
  };

  const handleItemBlur = async () => {
    if (itemValue?.itemid) return;

    const resolved = await resolvePartFromInput(itemInput, itemValue);
    if (resolved) {
      setItemValue(resolved);
      setItemInput(resolved.itemcode || "");
    }
  };

  const handlePartyBlur = () => {
    if (partyValue?.subledgerid) return;

    const resolved = resolvePartyFromInput(partyInput, partyValue);
    if (resolved) {
      setPartyValue(resolved);
      setPartyInput(getPartyLabel(resolved));
    }
  };

  const handleEntryKeyDown = async (
    event,
    nextRef,
    runBlurLookup = false,
    blurFn = null
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (runBlurLookup && blurFn) {
        await blurFn();
      }

      if (nextRef?.current) {
        nextRef.current.focus();
      }
    }
  };

  const addRow = async () => {
    const finalItem = await resolvePartFromInput(itemInput, itemValue);
    const finalParty = resolvePartyFromInput(partyInput, partyValue);

    if (!finalItem?.itemid) {
      toast.error("Select valid Item");
      return;
    }

    if (!String(againstitemsrlno || "").trim()) {
      toast.error("Item Serial No required");
      return;
    }

    if (!finalParty?.subledgerid) {
      toast.error("Select valid Party");
      return;
    }

    const newRow = {
      srl: rows.length + 1,
      ticketno: String(ticketno || "").trim(),
      itemid: finalItem.itemid || null,
      itemcode: finalItem.itemcode || "",
      itemname: finalItem.itemname || "",
      againstitemsrlno: String(againstitemsrlno || "").trim(),
      customername: String(customername || "").trim(),
      phono: String(phono || "").replace(/\D/g, ""),
      vehicleno: String(vehicleno || "").trim(),
      dateofsale: dateofsale ? dayjs(dateofsale).format("YYYY-MM-DD") : null,
      subledgerid: finalParty.subledgerid,
      partylabel: getPartyLabel(finalParty),
      remarks: String(rowRemarks || "").trim(),
    };

    setRows((prev) => [...prev, newRow]);
    resetEntryFields();
  };

  const processRowUpdate = async (newRow, oldRow) => {
    const matchedItem =
      allparts.find(
        (p) =>
          p.itemid === newRow.itemid ||
          String(p.itemcode || "").toUpperCase() ===
            String(newRow.itemcode || "").toUpperCase()
      ) || null;

    const matchedParty =
      parties.find(
        (p) =>
          p.subledgerid === newRow.subledgerid ||
          String(getPartyLabel(p)).toUpperCase() ===
            String(newRow.partylabel || "").toUpperCase()
      ) || null;

    const updatedRow = {
      ...newRow,
      itemid: matchedItem?.itemid || newRow.itemid || null,
      itemcode: matchedItem?.itemcode || newRow.itemcode || "",
      itemname: matchedItem?.itemname || newRow.itemname || "",
      againstitemsrlno: String(newRow.againstitemsrlno || "").trim(),
      phono: String(newRow.phono || "").replace(/\D/g, ""),
      subledgerid: matchedParty?.subledgerid || newRow.subledgerid || null,
      partylabel: matchedParty
        ? getPartyLabel(matchedParty)
        : String(newRow.partylabel || "").trim(),
    };

    if (!updatedRow.itemid) {
      throw new Error(`Item is mandatory at row ${updatedRow.srl}`);
    }

    if (!updatedRow.againstitemsrlno) {
      throw new Error(`Item Serial No is mandatory at row ${updatedRow.srl}`);
    }

    if (!updatedRow.subledgerid) {
      throw new Error(`Party is mandatory at row ${updatedRow.srl}`);
    }

    setRows((prev) =>
      prev.map((row) => (row.srl === updatedRow.srl ? updatedRow : row))
    );

    return updatedRow;
  };

  const handleDeleteClick = (id) => () => {
    if (window.confirm("Remove this row?")) {
      setRows((prevRows) => renumberRows(prevRows.filter((row) => row.srl !== id)));
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "action",
        type: "actions",
        headerClassName: "super-app-theme--header",
        headerName: "",
        width: 42,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ],
      },
      {
        field: "srl",
        headerName: "Srl",
        width: 55,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "ticketno",
        headerName: "Ticket Number",
        width: 200,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "itemcode",
        headerName: "Item",
        width: 300,
        //editable: true,
        headerClassName: "super-app-theme--header",
        renderCell: (params) =>
          `${params.row.itemcode || ""}${
            params.row.itemname ? ` - ${params.row.itemname}` : ""
          }`,
        renderEditCell: (params) => (
          <PartEditAutocomplete {...params} allparts={allparts} />
        ),
      },
      {
        field: "againstitemsrlno",
        headerName: "Serial Number",
        width: 150,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "customername",
        headerName: "Customer Name",
        width: 140,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "phono",
        headerName: "Phone",
        width: 105,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "vehicleno",
        headerName: "Vehicle",
        width: 150,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "dateofsale",
        headerName: "Sale Date",
        width: 120,
        editable: true,
        headerClassName: "super-app-theme--header",
        valueFormatter: (params) =>
          params?.value ? dayjs(params.value).format("DD/MM/YYYY") : "",
        renderEditCell: (params) => <DateEditCell {...params} />,
      },
      {
        field: "partylabel",
        headerName: "Dealer Name",
        width: 500,
       // editable: true,
        headerClassName: "super-app-theme--header",
        renderEditCell: (params) => (
          <PartyEditAutocomplete {...params} parties={parties} />
        ),
      },
      {
        field: "remarks",
        headerName: "Remarks",
        width: 500,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
    ],
    [allparts, parties]
  );

  const validateRows = () => {
    if (!rows.length) {
      toast.error("Add at least one row");
      return false;
    }

    for (const r of rows) {
      const matchedItem =
        r.itemid ||
        allparts.find(
          (p) =>
            String(p.itemcode || "").toUpperCase() ===
            String(r.itemcode || "").toUpperCase()
        )?.itemid;

      const matchedParty =
        r.subledgerid ||
        parties.find(
          (p) =>
            String(getPartyLabel(p)).toUpperCase() ===
            String(r.partylabel || "").toUpperCase()
        )?.subledgerid;

      if (!matchedItem) {
        toast.error(`Item missing at row ${r.srl}`);
        return false;
      }

      if (!String(r.againstitemsrlno || "").trim()) {
        toast.error(`Item Serial No missing at row ${r.srl}`);
        return false;
      }

      if (!matchedParty) {
        toast.error(`Party missing at row ${r.srl}`);
        return false;
      }
    }

    return true;
  };

  const prepareRowsForSave = () => {
    return rows.map((r, idx) => {
      const matchedItem =
        allparts.find(
          (p) =>
            p.itemid === r.itemid ||
            String(p.itemcode || "").toUpperCase() ===
              String(r.itemcode || "").toUpperCase()
        ) || null;

      const matchedParty =
        parties.find(
          (p) =>
            p.subledgerid === r.subledgerid ||
            String(getPartyLabel(p)).toUpperCase() ===
              String(r.partylabel || "").toUpperCase()
        ) || null;

      return {
        ...r,
        srl: idx + 1,
        itemid: matchedItem?.itemid || r.itemid || null,
        itemcode: matchedItem?.itemcode || r.itemcode || "",
        itemname: matchedItem?.itemname || r.itemname || "",
        againstitemsrlno: String(r.againstitemsrlno || "").trim(),
        phono: String(r.phono || "").replace(/\D/g, ""),
        subledgerid: matchedParty?.subledgerid || r.subledgerid || null,
        partylabel: matchedParty ? getPartyLabel(matchedParty) : r.partylabel || "",
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateRows()) return;

    setDisableSave(true);
    setPageLoading(true);

    try {
      const body = {
        docdate: docdate ? dayjs(docdate).format("YYYY-MM-DD") : null,
        reqremarks,
        rows: prepareRowsForSave(),
      };

      const response = await fetch("/warranty/req/insert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Save failed");
        setDisableSave(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Save failed");
      setDisableSave(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!docdetails?.headerid) {
      toast.error("Select document");
      return;
    }

    setPageLoading(true);

    try {
      const response = await fetch(`/warranty/req/data/${docdetails.headerid}`, {
        method: "GET",
        headers: { sptoken: userToken },
      });

      const jsonData = await response.json();

      if (jsonData.type === "error") {
        toast.error(jsonData.message || "Search failed");
        return;
      }

      setRows(normalizeRowsForGrid(jsonData.detaildata || [], allparts, parties));
      setDocDate(dayjs(jsonData.headerdata.docdate));
      setDocNo(jsonData.headerdata.docno || "");
      setReqRemarks(jsonData.headerdata.reqremarks || "");
    } catch (err) {
      console.log(err.message);
      toast.error("Search failed");
    } finally {
      setPageLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!docdetails?.headerid) {
      toast.error("Select document");
      return;
    }

    if (!validateRows()) return;

    setDisableUpdate(true);
    setPageLoading(true);

    try {
      const body = {
        docdate: docdate ? dayjs(docdate).format("YYYY-MM-DD") : null,
        reqremarks,
        rows: prepareRowsForSave(),
      };

      const response = await fetch(`/warranty/req/update/${docdetails.headerid}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Update failed");
        setDisableUpdate(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Update failed");
      setDisableUpdate(false);
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <>
      <Box>
        <Sidebar
          Title={"WARRANTY REQUEST"}
          expandedPanel={"panel2"}
          children={
            <Box
              component="form"
              sx={{
                width: "100%",
                "& .MuiCard-root": { borderRadius: 1.5 },
              }}
              autoComplete="off"
            >
              <Backdrop
                open={pageLoading || loadingParts || loadingParties}
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 999,
                  backdropFilter: "blur(1.5px)",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={24} />
                <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                  {loadingParts || loadingParties
                    ? "Loading master data..."
                    : "Processing..."}
                </Typography>
              </Backdrop>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={0.75}
                    alignItems={{ xs: "stretch", md: "center" }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <TextField
                      select
                      label="Mode"
                      value={mode}
                      onChange={handleModeChange}
                      sx={{ width: 86, ...compactInputSx }}
                      size="small"
                    >
                      <MenuItem value={1}>New</MenuItem>
                      <MenuItem value={2}>Edit</MenuItem>
                    </TextField>

                    <TextField
                      disabled
                      label="Doc No"
                      value={docno}
                      sx={{ width: 105, ...compactInputSx }}
                      size="small"
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Doc Date"
                        inputFormat="DD/MM/YYYY"
                        value={docdate}
                        onChange={(newValue) => setDocDate(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 120, ...compactInputSx }}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <TextField
                      label="Req Remarks"
                      value={reqremarks}
                      onChange={(e) => setReqRemarks(e.target.value)}
                      size="small"
                      sx={{ minWidth: 180, flex: 1, ...compactInputSx }}
                    />
                  </Stack>

                  {mode === 2 && (
                    <>
                      <Divider sx={{ my: 0.8 }} />
                      <Stack
                        spacing={0.75}
                        direction={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "stretch", md: "center" }}
                      >
                        <Autocomplete
                          disabled={searchdisabled}
                          id="doc-search"
                          value={docdetails}
                          filterOptions={filterOptions}
                          onChange={(event, newValue) => setDocDetails(newValue)}
                          options={docopts}
                          getOptionLabel={(option) => option.documentno || ""}
                          sx={{ minWidth: 220, flex: 1, ...compactAutoSx }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              placeholder="Search Document"
                              label="Document"
                            />
                          )}
                        />

                        <Button
                          disabled={searchbtndisabled}
                          variant="contained"
                          onClick={handleSearch}
                          startIcon={<SearchRoundedIcon />}
                          sx={{ borderRadius: 1.2, minWidth: 88, height: 31, fontSize: 12 }}
                        >
                          Search
                        </Button>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Stack
                    direction="row"
                    spacing={0.65}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <TextField
                      label="Ticket"
                      size="small"
                      value={ticketno}
                      onChange={(e) => setTicketNo(e.target.value)}
                      inputRef={ticketRef}
                      onKeyDown={(e) => handleEntryKeyDown(e, itemRef)}
                      sx={{ width: 90, ...compactInputSx }}
                    />

                    <Autocomplete
                      freeSolo
                      value={itemValue}
                      inputValue={itemInput}
                      filterOptions={filterOptions}
                      onInputChange={(event, newInputValue) => {
                        setItemInput(newInputValue);
                        if (!newInputValue) setItemValue(null);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setItemValue(null);
                          setItemInput(newValue);
                        } else {
                          setItemValue(newValue);
                          setItemInput(newValue?.itemcode || "");
                        }
                      }}
                      options={allparts}
                      loading={loadingParts}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.itemcode
                          ? `${option.itemcode} - ${option.itemname || ""}`
                          : "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.itemid === value?.itemid
                      }
                      sx={{ width: 220, ...compactAutoSx }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.itemid || option.itemcode}>
                          <Box sx={{ width: "100%" }}>
                            <Typography
                              sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.15 }}
                            >
                              {option.itemcode} - {option.itemname}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 10.25,
                                color: "text.secondary",
                                lineHeight: 1.1,
                              }}
                            >
                              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
                              {option.rackname || "N/A"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Item"
                          size="small"
                          inputRef={itemRef}
                          onBlur={handleItemBlur}
                          onKeyDown={(e) =>
                            handleEntryKeyDown(
                              e,
                              againstSrlRef,
                              true,
                              handleItemBlur
                            )
                          }
                        />
                      )}
                    />

                    <TextField
                      label="Srl No"
                      size="small"
                      value={againstitemsrlno}
                      onChange={(e) => setAgainstItemSrlNo(e.target.value)}
                      inputRef={againstSrlRef}
                      onKeyDown={(e) => handleEntryKeyDown(e, customerRef)}
                      sx={{ width: 95, ...compactInputSx }}
                    />

                    <TextField
                      label="Customer"
                      size="small"
                      value={customername}
                      onChange={(e) => setCustomerName(e.target.value)}
                      inputRef={customerRef}
                      onKeyDown={(e) => handleEntryKeyDown(e, phoneRef)}
                      sx={{ width: 120, ...compactInputSx }}
                    />

                    <TextField
                      label="Phone"
                      size="small"
                      value={phono}
                      onChange={(e) => setPhono(e.target.value.replace(/\D/g, ""))}
                      inputRef={phoneRef}
                      onKeyDown={(e) => handleEntryKeyDown(e, vehicleRef)}
                      sx={{ width: 105, ...compactInputSx }}
                    />

                    <TextField
                      label="Vehicle"
                      size="small"
                      value={vehicleno}
                      onChange={(e) => setVehicleNo(e.target.value)}
                      inputRef={vehicleRef}
                      onKeyDown={(e) => handleEntryKeyDown(e, partyRef)}
                      sx={{ width: 95, ...compactInputSx }}
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Sale Date"
                        inputFormat="DD/MM/YYYY"
                        value={dateofsale}
                        onChange={(newValue) => setDateOfSale(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 118, ...compactInputSx }}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <Autocomplete
                      freeSolo
                      value={partyValue}
                      inputValue={partyInput}
                      filterOptions={filterOptions}
                      options={parties}
                      loading={loadingParties}
                      onInputChange={(event, newInputValue) => {
                        setPartyInput(newInputValue);
                        if (!newInputValue) setPartyValue(null);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setPartyValue(null);
                          setPartyInput(newValue);
                        } else {
                          setPartyValue(newValue);
                          setPartyInput(getPartyLabel(newValue));
                        }
                      }}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return getPartyLabel(option);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.subledgerid === value?.subledgerid
                      }
                      sx={{ width: 300, ...compactAutoSx }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.subledgerid}>
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
                            {getPartyLabel(option)}
                          </Typography>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Party"
                          size="small"
                          inputRef={partyRef}
                          onBlur={handlePartyBlur}
                          onKeyDown={(e) =>
                            handleEntryKeyDown(e, rowRemarksRef, true, handlePartyBlur)
                          }
                        />
                      )}
                    />

                    <TextField
                      label="Remarks"
                      size="small"
                      value={rowRemarks}
                      onChange={(e) => setRowRemarks(e.target.value)}
                      inputRef={rowRemarksRef}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRow();
                        }
                      }}
                      sx={{ width: 120, ...compactInputSx }}
                    />

                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addRow}
                      sx={{
                        borderRadius: 1.2,
                        px: 1,
                        height: 31,
                        minWidth: 72,
                        fontSize: 11.5,
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      height: 392,
                      width: "100%",
                      "& .super-app-theme--header": {
                        backgroundColor: "#ebf5ff",
                        color: "#0d3557",
                        fontWeight: 800,
                        fontSize: 11.25,
                      },
                      "& .MuiDataGrid-cell": {
                        fontSize: 11.25,
                        py: 0,
                      },
                      "& .MuiDataGrid-footerContainer": {
                        minHeight: 36,
                      },
                    }}
                  >
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      getRowId={(row) => row.srl}
                      density="compact"
                      rowHeight={27}
                      headerHeight={29}
                      disableSelectionOnClick
                      editMode="cell"
                      experimentalFeatures={{ newEditingApi: true }}
                      processRowUpdate={processRowUpdate}
                      onProcessRowUpdateError={(error) => {
                        console.error(error);
                        toast.error(error?.message || "Failed to update row");
                      }}
                      components={{
                        Toolbar: () => <GridTopBar rowCount={rows.length} />,
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: "srl", sort: "asc" }],
                        },
                        pagination: {
                          pageSize: 10,
                        },
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1} justifyContent="center">
                {mode === 1 && (
                  <Button
                    disabled={disableSave}
                    onClick={handleSave}
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 1.2, minWidth: 90, height: 31, fontSize: 12 }}
                  >
                    Save
                  </Button>
                )}

                {mode === 2 && (
                  <Button
                    disabled={disableUpdate}
                    onClick={handleUpdate}
                    size="small"
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: 1.2, minWidth: 90, height: 31, fontSize: 12 }}
                  >
                    Update
                  </Button>
                )}
              </Stack>
            </Box>
          }
        />
        {redirectToLoginPage()}
      </Box>
    </>
  );
};

export default WarrantyReq;